// Storage helpers shared by both apps. Each app picks its own keys, but the
// "remember me?" semantics — localStorage when checked, sessionStorage when
// not — match the original behavior in admin.html and platform.html.

export interface StoredAuth {
  read(): string | null;
  write(value: string, remember: boolean): void;
  clear(): void;
}

export function createStoredAuth(key: string, rememberFlag: string): StoredAuth {
  return {
    read() {
      return sessionStorage.getItem(key) || localStorage.getItem(key) || null;
    },
    write(value, remember) {
      if (remember) {
        localStorage.setItem(key, value);
        localStorage.setItem(rememberFlag, "1");
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, value);
        localStorage.removeItem(key);
        localStorage.removeItem(rememberFlag);
      }
    },
    clear() {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
      localStorage.removeItem(rememberFlag);
    },
  };
}

// Tenant-side login also persists token expiry so we can sign the user out
// when the session expires (mirrors original logic in platform.html).
export interface StoredToken {
  read(): { token: string; expiresAt: number } | null;
  write(token: string, expiresInSeconds: number, remember: boolean): void;
  clear(): void;
}

export function createStoredToken(
  tokenKey: string,
  expiresKey: string,
  rememberFlag: string,
): StoredToken {
  const stores = () => ({
    local: localStorage,
    session: sessionStorage,
  });

  return {
    read() {
      const { local, session } = stores();
      const token = session.getItem(tokenKey) || local.getItem(tokenKey);
      const expRaw = session.getItem(expiresKey) || local.getItem(expiresKey);
      if (!token) return null;
      const expiresAt = expRaw ? Number(expRaw) : 0;
      if (!Number.isFinite(expiresAt) || expiresAt < Date.now()) {
        // expired — clean up
        local.removeItem(tokenKey);
        local.removeItem(expiresKey);
        session.removeItem(tokenKey);
        session.removeItem(expiresKey);
        return null;
      }
      return { token, expiresAt };
    },
    write(token, expiresInSeconds, remember) {
      const expiresAt = Date.now() + Math.max(0, expiresInSeconds) * 1000;
      const { local, session } = stores();
      if (remember) {
        local.setItem(tokenKey, token);
        local.setItem(expiresKey, String(expiresAt));
        local.setItem(rememberFlag, "1");
        session.removeItem(tokenKey);
        session.removeItem(expiresKey);
      } else {
        session.setItem(tokenKey, token);
        session.setItem(expiresKey, String(expiresAt));
        local.removeItem(tokenKey);
        local.removeItem(expiresKey);
        local.removeItem(rememberFlag);
      }
    },
    clear() {
      const { local, session } = stores();
      local.removeItem(tokenKey);
      local.removeItem(expiresKey);
      local.removeItem(rememberFlag);
      session.removeItem(tokenKey);
      session.removeItem(expiresKey);
    },
  };
}
